/**
 * Chrome applicatif : barre du haut, navigation, bandeau de mode développement.
 *
 * La barre est celle du studio (exiUI, coquille.md §1) — anatomie et ordre imposés :
 * marque · navigation inline · ⟨ml-auto⟩ · thème · compte. Chaque élément vient du contrat
 * `@exidis/ui` : `TOOLBAR`, `NAV_LINK_*`, `TOOLBAR_END`, les déroulants. Rien n'est écrit
 * à la main ici, c'est ce qui rend la barre identique d'un produit à l'autre.
 *
 * La langue ne figure PAS dans la barre : elle vit dans le menu du compte (§6.3).
 */

import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { usePreferences } from '../hooks/usePreferences.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { appConfirm } from '../dialogs/service.js';
import { onOrganizationsChanged } from '../lib/organizationsBus.js';
import {
  BRAND,
  BRAND_GLYPH,
  NAV_LINK_ACTIVE,
  NAV_LINK_IDLE,
  TAG,
  TOOLBAR,
  TOOLBAR_END,
} from '../ui/theme.js';
import GlypheExiPlan from './common/GlypheExiPlan.jsx';

import { nomEspace } from '../lib/nom-espace.js';
import MenuCompte from './common/MenuCompte.jsx';
import { NameFallbackBanner } from './NameFallbackBanner.jsx';
import MenuTheme from './common/MenuTheme.jsx';

/**
 * Entrée de navigation de l'organisation du compte (ORG-4 : 0 ou 1).
 *
 * `/me/organizations` n'est PAS filtré par statut : une adhésion `requested`/`suspended` y
 * figure, pour que le compte connaisse son état. Elle ne mène jamais à `/organisation/:id`, que
 * `loadOrganization` refuse en 404 hors adhésion active (I2) : l'entrée porte un badge d'état et
 * renvoie vers `/organisation/nouvelle`, qui explique la situation et permet de retirer une
 * demande en attente.
 */
function organizationEntry(organization, t) {
  if (!organization) return { to: '/organisation/nouvelle', label: t('nav.createOrganization') };
  if (organization.status === 'active') {
    return { to: `/organisation/${organization.id}`, label: nomEspace(organization, t) };
  }
  return {
    to: '/organisation/nouvelle',
    label: nomEspace(organization, t),
    badge:
      organization.status === 'requested'
        ? t('nav.organizationPending')
        : t('nav.organizationSuspended'),
  };
}

export function AppShell({ children }) {
  const { t } = useTranslation();
  const { user, bypass, logout } = useSession();
  const navigate = useNavigate();

  // Applique la largeur de page et le thème dès l'ouverture de la session — sur toutes les
  // pages, pas seulement /preferences. `save` est passé au déroulant de thème : sans cela, un
  // choix fait dans la barre n'était pas persisté et la réponse serveur le révoquait au
  // rechargement suivant. Cf. `hooks/usePreferences.js`.
  const { save: savePreferences } = usePreferences();

  // `organizationsTick` force un rechargement quand la liste d'organisations change (création
  // depuis `/organisation/nouvelle`, suppression depuis `OrganizationPage`) : ce composant est
  // un ancêtre de ces écrans dans l'arbre, il n'a donc pas d'autre moyen de savoir que la liste
  // a changé.
  const [organizationsTick, setOrganizationsTick] = useState(0);
  useEffect(() => onOrganizationsChanged(() => setOrganizationsTick((tick) => tick + 1)), []);
  const { data: organizationsData } = useAsync(
    (signal) => api.get('/me/organizations', { signal }),
    [organizationsTick],
  );
  // ORG-4 : au plus une organisation par compte. `/me/organizations` n'en rend que 0 ou 1 —
  // l'espace personnel n'y figure jamais (ORG-8). Sans organisation, l'entrée invite à en créer
  // une.
  const organization = organizationsData?.data?.[0] ?? null;

  // « Profil » n'est plus une destination de la barre : il vit dans le menu du compte.
  const NAV = [
    { to: '/projets', label: t('nav.projects') },
    { to: '/vue-ensemble', label: t('nav.overview') },
    { to: '/equipes', label: t('nav.teams') },
    organizationEntry(organization, t),
  ];

  const onLogout = async () => {
    if (
      !(await appConfirm(t('session.confirmLogout'), {
        confirmLabel: t('session.logoutConfirmLabel'),
      }))
    )
      return;
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-paper">
      <a className="skip-link" href="#contenu">
        {t('a11y.skipToContent')}
      </a>

      {bypass && (
        <div
          role="status"
          className="border-b-2 border-accent-trait bg-accent-wash px-3 py-1.5 text-center font-mono text-[11px] uppercase tracking-eyebrow text-accent-ink"
        >
          {t('session.devModeBanner')}
        </div>
      )}

      <NameFallbackBanner />

      <header className={TOOLBAR}>
        <NavLink to="/projets" className={BRAND}>
          <span className={BRAND_GLYPH} aria-hidden="true">
            <GlypheExiPlan />
          </span>
          exiPlan
        </NavLink>

        <nav className="flex items-center gap-1" aria-label={t('a11y.mainNav')}>
          {NAV.map(({ to, label, badge }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE : NAV_LINK_IDLE)}
            >
              {label}
              {badge && <span className={`${TAG} ml-1.5`}>{badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className={TOOLBAR_END}>
          <MenuTheme onPersist={user ? (theme) => savePreferences({ theme }) : null} />
          {user && <MenuCompte user={user} onLogout={onLogout} />}
        </div>
      </header>

      {/* AUCUNE borne de largeur ici : elle enfermait l'en-tête de page de toutes les pages,
          dont le trait 2 px s'arrêtait alors en l'air au lieu de courir d'un bord à l'autre
          (contrat exiUI, coquille.md §5). La borne est portée par `PageHeader` sur son contenu
          seul, et par `PageBody` sur le corps — voir `components/common.jsx`. */}
      <main id="contenu" className="min-h-[calc(100vh-2.75rem)]">
        {children}
      </main>
    </div>
  );
}
